import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

export default function SlideInPanel({
  isOpen,
  onClose,
  title,
  children,
  width = 'w-full sm:w-[480px]',
}) {
  const panelRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm transition-opacity duration-300"
      data-testid="slide-panel-overlay"
    >
      <div
        ref={panelRef}
        className={`
          fixed
          top-0
          right-0
          h-full
          max-w-full
          ${width}
          bg-card
          border-l
          border-border
          shadow-2xl
          transform
          transition-transform
          duration-300
          ease-out
          overflow-hidden
          flex
          flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ animation: 'slideInRight 0.3s ease-out' }}
        data-testid="slide-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border flex-shrink-0">
          <h2 className="text-base sm:text-lg font-semibold truncate">
            {title}
          </h2>

          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors shrink-0"
            data-testid="close-slide-panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}